export const SUPPORTED_LOCALES = ["de", "en", "fr", "it"] as const;
export type LocaleCode = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: LocaleCode = SUPPORTED_LOCALES[0];

const INTERNAL_LOCALE_PREFIX = new RegExp(`^/(?:${SUPPORTED_LOCALES.join("|")})(?:/|$|\\?|#)`);
const EXTERNAL_OR_SPECIAL_PREFIX = /^(?:[a-zA-Z][a-zA-Z\d+.-]*:|\/\/|#)/;

export function isLocaleCode(value: unknown): value is LocaleCode {
  return typeof value === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function resolveCurrentLocale(paramsLang: unknown, pathname: string | null): LocaleCode {
  if (isLocaleCode(paramsLang)) {
    return paramsLang;
  }

  const firstSegment = pathname?.split("/")[1];
  if (isLocaleCode(firstSegment)) {
    return firstSegment;
  }

  return DEFAULT_LOCALE;
}

export function localizePath(pathname: string, locale: LocaleCode): string {
  if (!pathname || EXTERNAL_OR_SPECIAL_PREFIX.test(pathname)) {
    return pathname;
  }

  // Normalize internal relative paths to absolute
  const normalizedPath = pathname.startsWith("/") ? pathname : `/${pathname}`;

  // Skip if already has a locale prefix
  if (INTERNAL_LOCALE_PREFIX.test(normalizedPath)) {
    return normalizedPath;
  }

  if (locale === DEFAULT_LOCALE) {
    return normalizedPath;
  }

  return normalizedPath === "/" ? `/${locale}` : `/${locale}${normalizedPath}`;
}

export function stripLocalePrefix(pathname: string): string {
  if (!pathname) {
    return "/";
  }

  const normalizedPath = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const matchedLocale = SUPPORTED_LOCALES.find(
    (locale) => normalizedPath.startsWith(`/${locale}/`) || normalizedPath === `/${locale}`,
  );

  if (!matchedLocale) {
    return normalizedPath;
  }

  const withoutLocale = normalizedPath.slice(matchedLocale.length + 1);
  return withoutLocale ? `/${withoutLocale.replace(/^\/+/, "")}` : "/";
}
